import React, { useRef, useState, useEffect, useCallback, memo } from "react";
import PropTypes from "prop-types";
import { throttle } from "lodash";
import videojs from "video.js";
import { useStoreActions, useStoreState } from "easy-peasy";
import { getSeconds } from "../../utils/timeUtils";
import { MARKER_TYPES } from "../../constants";
import { isMobile } from "../../utils/ResponsiveUtils";
import analytics from "../../utils/analytics";

function DemoStage({
  videoUrl,
  videoType,
  videoPoster,
  hasOutro,
  hasIntro,
  isMuted,
  sizzle,
  product,
}) {
  const CONTAINER_ID = "interActiveExperienceStagePlayer";
  const videoPlayerRef = useRef(null);
  const trackSizzleStartRef = useRef(null);
  const trackSizzleEndRef = useRef(null);
  const trackStageViewthroughRef = useRef(null);
  const trackVideoEndedRef = useRef(null);
  const { player: videoPlayer, isPlaying } = useStoreState(
    (state) => state.stage
  );
  const activeFeature = useStoreState((state) => state.features.activeFeature);
  const features = useStoreState((state) => state.features.features);
  const { player: bgmPlayer } = useStoreState((state) => state.bgm);
  const {
    isPlaying: isExperiencePlaying,
    isMuted: isExperienceMuted,
  } = useStoreState((state) => state.experience);
  const { startTime } = useStoreState((state) => state.demo);

  const {
    setPlaying,
    setPlayer: setStagePlayer,
    setSkipVisible,
    setCurrentPlayingTime,
  } = useStoreActions((actions) => actions.stage);

  const setActiveFeature = useStoreActions(
    (actions) => actions.features.setActiveFeature
  );

  const setNavVisibility = useStoreActions(
    (actions) => actions.navigation.setNavVisibility
  );
  const setExperiencePlaying = useStoreActions(
    (actions) => actions.experience.setPlaying
  );
  const { setPlaying: setBgmPlaying } = useStoreActions(
    (actions) => actions.bgm
  );
  const setIntroVisible = useStoreActions(
    (actions) => actions.intro.setVisible
  );
  const setOutroVisible = useStoreActions(
    (actions) => actions.outro.setVisible
  );
  const setDemoVisible = useStoreActions((actions) => actions.demo.setVisible);
  const setResetExperience = useStoreActions(
    (actions) => actions.experience.setResetExperience
  );

  const totalPlayed = useStoreState((state) => state.analytics.totalPlayed);
  const currentIntervalStart = useStoreState(
    (state) => state.analytics.currentIntervalStart
  );
  const currentIntervalEnd = useStoreState(
    (state) => state.analytics.currentIntervalEnd
  );
  const playCount = useStoreState((state) => state.analytics.playCount);

  const {
    setTotalPlayed,
    setCurrentIntervalStart,
    setCurrentIntervalEnd,
  } = useStoreActions((actions) => actions.analytics);

  const [sizzleMarkers, setSizzleMarkers] = useState([]);
  const showNavRef = useRef(null);

  const getActiveFeature = useCallback(
    (curTime) => {
      return features.findIndex(
        (feature) =>
          curTime > getSeconds(feature.start || "00:00") &&
          curTime <= getSeconds(feature.end || "00:00")
      );
    },
    [features]
  );

  const trackSizzleStart = useCallback(() => {
    if (trackSizzleStartRef && trackSizzleStartRef.current) {
      clearTimeout(trackSizzleStartRef.current);
    }
    trackSizzleStartRef.current = setTimeout(() => {
      if (product && product.name) {
        analytics.trackSizzleStart(product.name);
      }
    }, 450);
  }, [trackSizzleStartRef, product]);

  const trackSizzleEnd = useCallback(() => {
    if (trackSizzleEndRef && trackSizzleEndRef.current) {
      clearTimeout(trackSizzleEndRef.current);
    }
    trackSizzleEndRef.current = setTimeout(() => {
      if (product && product.name) {
        analytics.trackSizzleEnd(product.name);
      }
    }, 450);
  }, [trackSizzleEndRef, product]);

  const trackStageViewthrough = useCallback(
    (index) => {
      if (trackStageViewthroughRef && trackStageViewthroughRef.current) {
        clearTimeout(trackStageViewthroughRef.current);
      }
      setTimeout(() => {
        analytics.trackStageViewthrough(index + 1, features[index].title);
      }, 450);
    },
    [trackStageViewthroughRef, features]
  );
  const trackVideoEnded = useCallback(() => {
    if (trackVideoEndedRef && trackVideoEndedRef.current) {
      clearTimeout(trackVideoEndedRef.current);
    }
    trackVideoEndedRef.current = setTimeout(() => {
      if (product && product.name) {
        analytics.trackVideoEnd(product.name, Math.round(totalPlayed));
      }
    }, 1000);
  }, [trackVideoEndedRef, product, totalPlayed]);

  const performEvent = useCallback(
    (eventType) => {
      switch (eventType) {
        case MARKER_TYPES.SHOW_NAV:
          setNavVisibility(true);
          break;
        case MARKER_TYPES.HIDE_NAV:
          setNavVisibility(false);
          break;
        case MARKER_TYPES.SHOW_SKIP:
          setSkipVisible(true);
          break;
        case MARKER_TYPES.HIDE_SKIP:
          setSkipVisible(false);
          break;
        case MARKER_TYPES.SIZZLE_START:
          trackSizzleStart();
          break;
        case MARKER_TYPES.SIZZLE_END:
          trackSizzleEnd();
          break;
        default:
          break;
      }
    },
    [setNavVisibility, setSkipVisible, trackSizzleStart, trackSizzleEnd]
  );

  const checkIfEvent = useCallback(
    (curTime, markersArr, threshold = 0.4) => {
      const lowerLimit = curTime - threshold;
      const upperLimit = curTime + threshold;

      if (markersArr && markersArr.length > 0) {
        markersArr.forEach((marker) => {
          if (
            getSeconds(marker.mark) >= lowerLimit &&
            getSeconds(marker.mark) <= upperLimit
          ) {
            performEvent(marker.type);
          }
        });
      }
    },
    [performEvent]
  );

  const updateActiveFeature = useCallback(
    (curTime) => {
      // Track viewthrough event
      // if on end of current feature, track viewthrough of next one
      if (activeFeature >= 0) {
        if (
          features[activeFeature] &&
          features[activeFeature].end &&
          curTime < getSeconds(features[activeFeature].end) &&
          curTime > getSeconds(features[activeFeature].end) - 0.5 &&
          activeFeature < features.length - 1
        ) {
          // track viewthrough
          trackStageViewthrough(activeFeature + 1);
        }
      } else if (
        // First feature track, coming from intro
        activeFeature < 0 &&
        features[0] &&
        features[0].start &&
        curTime < getSeconds(features[0].start) &&
        curTime > getSeconds(features[0].start) - 0.5
      ) {
        trackStageViewthrough(0);
      }

      const currentActiveFeatIndex = getActiveFeature(curTime);
      if (
        currentActiveFeatIndex >= 0 &&
        currentActiveFeatIndex !== activeFeature
      ) {
        setActiveFeature(currentActiveFeatIndex);
      }
    },
    [
      activeFeature,
      getActiveFeature,
      setActiveFeature,
      features,
      trackStageViewthrough,
    ]
  );

  const createPlayInterval = useCallback(
    (time) => {
      setCurrentIntervalStart(time);
    },
    [setCurrentIntervalStart]
  );

  const closePlayInterval = useCallback(
    (time) => {
      setCurrentIntervalEnd(time);
    },
    [setCurrentIntervalEnd]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTimeUpdate = useCallback(
    throttle(() => {
      const cT = videoPlayer.currentTime();
      setCurrentPlayingTime(cT);
      updateActiveFeature(cT);
      const currentFeat = features[activeFeature < 0 ? 0 : activeFeature];
      const { markers: currentFeatMarkers } = currentFeat || [];
      checkIfEvent(cT, currentFeatMarkers, 0.4);
      checkIfEvent(cT, sizzleMarkers, 0.45);
    }, 500),
    [
      videoPlayer,
      activeFeature,
      updateActiveFeature,
      setCurrentPlayingTime,
      checkIfEvent,
      features,
      sizzleMarkers,
    ]
  );

  const handlePlay = useCallback(() => {
    if (videoPlayer) {
      createPlayInterval(videoPlayer.currentTime());
    }
  }, [videoPlayer, createPlayInterval]);

  const handleSeeked = useCallback(() => {
    if (videoPlayer) {
      createPlayInterval(videoPlayer.currentTime());
    }
  }, [videoPlayer, createPlayInterval]);

  const handlePlaying = useCallback(() => {
    if (!isPlaying) {
      setPlaying(true);
    }
  }, [setPlaying, isPlaying]);

  const handlePaused = useCallback(() => {
    if (isPlaying && videoPlayer.paused()) {
      setPlaying(false);
    }
    closePlayInterval(videoPlayer.currentTime());
  }, [setPlaying, isPlaying, videoPlayer, closePlayInterval]);

  const handleStageClick = (event) => {
    if (event.keyCode) {
      return;
    }
    if (showNavRef.current) {
      clearTimeout(showNavRef.current);
    }
    setNavVisibility(true);
    showNavRef.current = setTimeout(() => {
      setNavVisibility(false);
    }, 3000);
  };

  const handleStageHover = () => {
    setNavVisibility(true);
  };

  const resetExperience = useCallback(() => {
    if (videoPlayer) {
      videoPlayer.pause();
      setPlaying(false);
      videoPlayer.currentTime(0);
      videoPlayer.hasStarted(false);
    }
    if (bgmPlayer) {
      bgmPlayer.pause();
      setBgmPlaying(false);
      bgmPlayer.currentTime(0);
    }
    setExperiencePlaying(false);
    setActiveFeature(-1);
    setTotalPlayed(0);
    setCurrentIntervalStart(0);
    setCurrentIntervalEnd(0);
  }, [
    setPlaying,
    videoPlayer,
    bgmPlayer,
    setBgmPlaying,
    setExperiencePlaying,
    setActiveFeature,
    setTotalPlayed,
    setCurrentIntervalEnd,
    setCurrentIntervalStart,
  ]);

  const handleEnded = useCallback(() => {
    setDemoVisible(false);
    setActiveFeature(-1);
    if (hasOutro) {
      setTimeout(() => {
        setOutroVisible(true);
      }, 650);
    } else if (hasIntro) {
      setTimeout(() => {
        resetExperience();
        setIntroVisible(true);
      }, 650);
    } else {
      resetExperience();
      setTimeout(() => {
        setDemoVisible(true);
      }, 1250);
    }
  }, [
    setActiveFeature,
    hasOutro,
    hasIntro,
    resetExperience,
    setDemoVisible,
    setIntroVisible,
    setOutroVisible,
  ]);

  const cleanUp = useCallback(() => {
    if (videoPlayer) {
      setStagePlayer(null);
      setActiveFeature(-1);
    }
  }, [setActiveFeature, setStagePlayer, videoPlayer]);

  // Update resetExperience in store
  useEffect(() => {
    setResetExperience(resetExperience);
    return () => {};
  }, [resetExperience, setResetExperience]);

  useEffect(() => {
    if (videoUrl) {
      setStagePlayer(
        videojs(videoPlayerRef.current, {
          html5: {
            hls: {
              overrideNative: true,
            },
          },
          muted: isMuted,
          controls: false,
          nativeControlsForTouch: false,
          poster: videoPoster || null,
          sources: [
            {
              src: videoUrl,
              type: videoType || "application/x-mpegURL",
            },
          ],
        })
      );
    }

    return () => {};
  }, [videoUrl, videoType, setStagePlayer, isMuted, videoPoster]);

  useEffect(() => {
    if (videoPlayer) {
      videoPlayer.on("timeupdate", handleTimeUpdate);
      videoPlayer.on("play", handlePlay);
      videoPlayer.on("playing", handlePlaying);
      videoPlayer.on("pause", handlePaused);
      videoPlayer.on("seeked", handleSeeked);
      videoPlayer.on("ended", handleEnded);
      if (!isExperiencePlaying && startTime && !isMobile()) {
        videoPlayer.currentTime(startTime);
      }
      if (!isMuted) {
        if (isExperienceMuted) {
          videoPlayer.muted(true);
        } else {
          videoPlayer.muted(false);
        }
      }
    }
    return () => {
      if (videoPlayer) {
        videoPlayer.off("timeupdate", handleTimeUpdate);
        videoPlayer.off("play", handlePlay);
        videoPlayer.off("playing", handlePlaying);
        videoPlayer.off("pause", handlePaused);
        videoPlayer.off("seeked", handleSeeked);
        videoPlayer.off("ended", handleEnded);
      }
    };
  }, [
    videoPlayer,
    handleTimeUpdate,
    handlePlay,
    handlePlaying,
    handlePaused,
    handleSeeked,
    handleEnded,
    isExperiencePlaying,
    startTime,
    isMuted,
    isExperienceMuted,
  ]);

  useEffect(() => {
    if (videoPlayer) {
      if (isPlaying) {
        videoPlayer.play();
      } else {
        videoPlayer.pause();
      }
    }
    return () => {};
  }, [videoPlayer, isPlaying]);

  useEffect(() => {
    if (sizzle.start && sizzle.end) {
      setSizzleMarkers([
        { type: MARKER_TYPES.SIZZLE_START, mark: sizzle.start },
        { type: MARKER_TYPES.SIZZLE_END, mark: sizzle.end },
      ]);
    }
    return () => {};
  }, [sizzle, setSizzleMarkers]);

  useEffect(() => {
    if (
      currentIntervalEnd !== 0 &&
      currentIntervalEnd - currentIntervalStart > 0
    ) {
      const curInterval = currentIntervalEnd - currentIntervalStart;
      const newTotal = totalPlayed + curInterval;
      setTotalPlayed(newTotal);
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIntervalEnd, currentIntervalStart, setTotalPlayed]);

  useEffect(() => {
    if (videoPlayer && videoPlayer.ended()) {
      trackVideoEnded();
    } else if (
      videoPlayer &&
      videoPlayer.paused() &&
      !videoPlayer.ended() &&
      totalPlayed > 0
    ) {
      if (product && product.name) {
        analytics.trackVideoPause(product.name, Math.round(totalPlayed));
      }
    }
    return () => {};
  }, [totalPlayed, videoPlayer, trackVideoEnded, product]);

  // Track play event based on when playCount changes
  useEffect(() => {
    if (playCount !== 0) {
      // track play event
      if (product && product.name) {
        analytics.trackPlay(product.name, playCount);
      }
    }
    return () => {};
  }, [playCount, product]);

  useEffect(() => {
    return () => {
      cleanUp();
    };
  }, [cleanUp]);

  return (
    <div id={CONTAINER_ID}>
      <div
        className="demo__stage"
        role="button"
        onClick={handleStageClick}
        onTouchStart={handleStageClick}
        onKeyPress={handleStageClick}
        tabIndex={-1}
        onMouseOver={handleStageHover}
        onFocus={handleStageHover}
      >
        <div className="demo-station">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoPlayerRef}
            className="
                  video-js
                  vjs-16-9
                  demo-stage__player
                  js-demo__stage-player
                  vjs-default-skin"
            playsInline
          />
        </div>
      </div>
      <style jsx="true">
        {`
          #${CONTAINER_ID} .demo__stage {
            width: 100% !important;
            height: 100% !important;
            cursor: pointer !important;
          }
          #${CONTAINER_ID} .demo__stage:focus {
            outline: 3px solid #cc0000 !important;
          }

          #${CONTAINER_ID} .demo-stage__player {
            position: absolute !important;
            z-index: 0 !important;
            top: 0 !important;
            left: 0 !important;

            width: 100% !important;
            height: 100% !important;
            // transition: all 0.3s ease-out !important;
          }
          #${CONTAINER_ID} .demo-stage__player .vjs-poster {
            ${videoPoster &&
            `
              background-image: url('${videoPoster}') !important;
            `}
          }
          #${CONTAINER_ID} .demo-stage__player.is-visible {
            z-index: 1 !important;

            opacity: 1 !important;
          }
        `}
      </style>
    </div>
  );
}

DemoStage.propTypes = {
  videoUrl: PropTypes.string,
  videoType: PropTypes.string,
  videoPoster: PropTypes.string,
  hasOutro: PropTypes.bool,
  hasIntro: PropTypes.bool,
  isMuted: PropTypes.bool,
  sizzle: PropTypes.object,
  product: PropTypes.object,
};

DemoStage.defaultProps = {
  videoUrl: "",
  videoType: "",
  videoPoster: "",
  hasOutro: false,
  hasIntro: false,
  isMuted: true,
  sizzle: {},
  product: {},
};

const arePropsAreEqual = (prevProps, nextProps) => {
  return (
    prevProps.videoUrl === nextProps.videoUrl &&
    prevProps.videoType === nextProps.videoType &&
    prevProps.videoPoster === nextProps.videoPoster &&
    prevProps.hasOutro === nextProps.hasOutro &&
    prevProps.hasIntro === nextProps.hasIntro &&
    prevProps.isMuted === nextProps.isMuted
  );
};

export default memo(DemoStage, arePropsAreEqual);
